import { GUIDE } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import type { CommandItem } from "@sagot/ui";
import { useEffect, useMemo, useState } from "react";
import { toGuideCommandItems } from "../../shell/nav-model";
import type { GuideContent } from "./content";

/** The guide content is its own chunk (large, English-only text), fetched on demand. */
const loadGuideContent = (): Promise<GuideContent> => import("./content").then((module) => module.GUIDE_CONTENT);

/**
 * The guide content once it has loaded: right away when `wanted` (the palette opened), otherwise a moment
 * after the console mounts, so the first Ctrl+K already lists the guide without delaying first paint.
 */
export function useGuideContent(wanted: boolean): GuideContent | null {
  const [content, setContent] = useState<GuideContent | null>(null);
  const [idle, setIdle] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setIdle(true);
    }, GUIDE.preloadDelayMs);
    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  const load = wanted || idle;
  useEffect(() => {
    if (!load || content) return;
    let current = true;
    void loadGuideContent().then((loaded) => {
      if (current) setContent(loaded);
    });
    return () => {
      current = false;
    };
  }, [load, content]);

  return content;
}

/** Guide topics as command-palette items, grouped as "User Guide". Empty until the content has loaded. */
export function useGuidePaletteItems(ws: string, wanted: boolean): CommandItem[] {
  const { t } = useTranslation();
  const content = useGuideContent(wanted);
  const group = t("guide.paletteGroup");
  return useMemo(() => (content ? toGuideCommandItems(content.topics, content.modules, ws, group) : []), [content, ws, group]);
}
